import { PlusOutlined } from '@ant-design/icons';
import {
    Breadcrumb,
    Button,
    Col,
    Form,
    Input,
    Row,
    Select,
    Upload,
    message
} from 'antd';
import { useEffect, useState } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import AutoOpenSelect from '../SelectComponent/AutoOpenSelect';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ReactComponent as Back } from '../../Images/Back.svg';
import { fetchUserById, updateUser } from '../../slices/user';
import { fetchUserGroupList, resetUserGroupList } from '../../slices/user-group';

const { Option } = Select;

function EditUser() {
    const [form] = Form.useForm();
    let navigate = useNavigate();
    const { id } = useParams();
    const [btnLoading, setBtnLoading] = useState(false);
    const [user, setUser] = useState('');
    const [fileList, setFileList] = useState([]);
    const dispatch = useDispatch();

    const { list: userGroupList } = useSelector((state) => state.userGroup);

    useEffect(() => {
        dispatch(resetUserGroupList());
        dispatch(fetchUserGroupList({ page: 1, pagesize: 10 }));
    }, []);

    const getFileType = (fileName) => {
        const extension = fileName.split('.').pop().toLowerCase(); // Get the file extension
        switch (extension) {
            case 'jpeg':
            case 'jpg':
                return 'image/jpeg';
            case 'png':
                return 'image/png';
            default:
                return false; // Fallback for unknown types
        }
    };

    useEffect(() => {
        const fetchUser = async () => {
            try {
                dispatch(fetchUserById(id))
                    .unwrap()
                    .then((response) => {
                        if (response) {
                            const user = response;
                            setUser(user);
                            const selectedPressIds = Array.isArray(user.groups)
                                ? user.groups.map((group) => group)
                                : [];
                            form.setFieldsValue({
                                username: user.username,
                                first_name: user.first_name,
                                last_name: user.last_name,
                                email: user.email,
                                phone: user.phone,
                                groups: selectedPressIds
                            });
                            setFileList(
                                user.profile_image
                                    ? [
                                        {
                                            uid: "-1", // Unique identifier for the file
                                            name: user.profile_image,
                                            url: user.profile_image,
                                            type: getFileType(user.profile_image),
                                            status: "done",
                                        },
                                    ]
                                    : []
                            );
                        }
                    })
                    .catch((error) => {
                        console.error("");
                    });
            } catch (error) {
                console.error("");
            }
        };

        fetchUser();
    }, [id, form]);

    const validateFileList = () => {
        const fileType = fileList[0]?.type;
        if (fileList.length > 0 && !["image/jpeg", "image/jpg", "image/png"].includes(fileType)) {
            return Promise.reject(new Error("Only JPG, JPEG, or PNG files are allowed!"));
        }
        return Promise.resolve();
    };

    const handleFileChange = ({ fileList: newFileList }) => {
        setFileList(newFileList);
    };

    const handleFinish = (values) => {
        setBtnLoading(true);
        let profilePic = fileList[0]?.originFileObj || (fileList[0]?.url ? user?.profile_image : null);
        dispatch(updateUser({ id: id, values, profilePic }))
            .unwrap()
            .then((result) => {
                setBtnLoading(false);
                if (result) {
                    message.success('User Updated Successfully!');
                    navigate('/manage-user');
                }
            })
            .catch((error) => {
                setBtnLoading(false);
                if (typeof error === 'object' && error !== null) {
                    const messages = [];
                    for (const key in error) {
                        if (Array.isArray(error[key])) {
                            messages.push(`${key}: ${error[key].join(', ')}`);
                        }
                    }

                    if (messages.length > 0) {
                        message.error(messages.join('; '));
                    } else {
                        message.error('An unknown error occurred.');
                    }
                } else if (typeof error === 'string') {
                    message.error(error);
                } else if (error instanceof Error) {
                    message.error(error.message);
                } else {
                    message.error('An unknown error occurred.');
                }
            });
    };

    return (
        <>
            <div className='sticyaction'>
                <Row className="pagenamerow mb-0" justify="space-between" align="middle">
                    <Col>
                        <h2 style={{ margin: 0 }}>Edit User</h2>
                        <div className="bredcrumbwrp">
                            <Link to="/manage-user" className="back">
                                <Back /> BACK
                            </Link>
                            <Breadcrumb
                                items={[
                                    { title: <Link to="/dashboard">Home</Link> },
                                    { title: <Link to="/manage-user">Users</Link> },
                                    { title: 'Edit User' },
                                ]}
                            />
                        </div>
                    </Col>
                </Row>
            </div>
            <div className='formwrapper'>
            <Form layout="vertical" form={form} onFinish={handleFinish} autoComplete="off">
                <Row gutter={[16, 16]} className="viewform userprofile">
                    <Col xs={24} sm={3}>
                        <Form.Item label="Profile Image" name="profile_image" style={{ margin: "0" }}
                            rules={[
                                {
                                    validator: validateFileList,
                                },
                            ]}>
                            <Upload
                                beforeUpload={() => false} // Prevent automatic upload
                                listType="picture-card"
                                accept=".jpeg,.jpg,.png"
                                maxCount={1} // Limit to 1 file
                                onChange={handleFileChange} // Update the state
                                fileList={fileList} // Bind fileList state
                            >
                                {fileList.length < 1 && ( // Show placeholder only if no files uploaded
                                    <div>
                                        <PlusOutlined />
                                        <div
                                            style={{
                                                marginTop: 8,
                                            }}
                                        >
                                            Upload
                                        </div>
                                    </div>
                                )}
                            </Upload>
                        </Form.Item>
                    </Col>
                      <Col xs={24} sm={21} >
                        <Row gutter={16}>
                            <Col xs={24} sm={12} xl={5}>
                                <Form.Item
                                    label="Username"
                                    name="username"
                                    rules={[
                                        { required: true, message: 'Please enter username' },
                                        {
                                            pattern: /^[A-Za-z0-9@.+\-_]+$/,
                                            message: 'Only letters, digits, and @/./+/-/_ are allowed',
                                        },
                                    ]}
                                >
                                    <Input placeholder="Enter User Name" 
                                    onChange={(e) => {
                                        let value = e.target.value;
                                        value = value.replace(/[^A-Za-z0-9@.+\-_]/g, '');
                                        const formattedValue = value.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
                                        form.setFieldsValue({ username: formattedValue }); // Update Form state
                                    }} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} xl={5}>
                                <Form.Item
                                    label="First Name"
                                    name="first_name"
                                    rules={[
                                        { required: true, message: 'Please enter first name' },
                                        {
                                            pattern: /^[A-Za-z]+$/,
                                            message: 'Only alphabets are allowed',
                                        },
                                    ]}
                                >
                                    <Input placeholder="Enter First Name" onChange={(e) => {
                                        let value = e.target.value;
                                        value = value.replace(/[^A-Za-z]/g, '');
                                        const formattedValue = value.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
                                        form.setFieldsValue({ first_name: formattedValue }); // Update Form state
                                    }} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} xl={5}>
                                <Form.Item
                                    label="Last Name"
                                    name="last_name"
                                    rules={[
                                        { required: true, message: 'Please enter last name' },
                                        {
                                            pattern: /^[A-Za-z]+$/,
                                            message: 'Only alphabets are allowed',
                                        },
                                    ]}
                                >
                                    <Input placeholder="Enter Last Name" 
                                    onChange={(e) => {
                                        let value = e.target.value;
                                        value = value.replace(/[^A-Za-z]/g, ''); 
                                        const formattedValue = value.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
                                        form.setFieldsValue({ last_name: formattedValue }); // Update Form state
                                    }} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} xl={6}>
                                <Form.Item
                                    label="Email Address"
                                    name="email"
                                    rules={[
                                        { required: true, message: 'Please enter email' },
                                        { type: 'email', message: 'Please enter a valid email address' },
                                    ]}
                                >
                                    <Input placeholder="Enter Email Address" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={4}>
                                <Form.Item
                                    label="Phone Number"
                                    name="phone"
                                    rules={[
                                        {
                                            required: true,
                                            message: 'Please enter your phone number!',
                                        },
                                    ]}
                                >
                                    <PhoneInput
                                        country={'in'} // Default country (India)
                                        enableSearch={true} // Enable searching for countries
                                        placeholder="Enter phone number"
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} xl={5}>
                                <Form.Item
                                    label="Role"
                                    name="groups"
                                    rules={[
                                        {
                                            required: true,
                                            message: 'Please select role',
                                        },
                                    ]}
                                >
                                    <AutoOpenSelect placeholder="Select Role" mode="multiple">
                                        {userGroupList.map((el) => (
                                            <Option key={el.id} value={el.name}>
                                                {el.name}
                                            </Option>
                                        ))}
                                    </AutoOpenSelect>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Col>
                </Row>

                <div className="flex justify-end">
                    <Form.Item className="mb-0">

                        <Button type="primary" htmlType="submit" loading={btnLoading}>
                            Update User
                        </Button>

                    </Form.Item>
                </div>
            </Form >
            </div>
        </>
    );
}

export default EditUser;
